import { useCallback, useEffect, useState } from "react"
import { SSE } from "utils/SSE"
import { useAuth } from "contexts/Auth"
import { useCall } from "contexts/Call"
import { useProfile } from "contexts/Profile"
import { usePureCallback } from "hooks/usePureCallback"
import { dataService } from "utils/dataService"
import { logger } from "utils/logger"
import { toRedacted } from 'martti-agent-services'

export const OUTSOURCE = "OUTSOURCE"
export const OVER_PHONE = "OPI"
export const UNKNOWN = "UNKNOWN"

export const mergeCallSegments = (current, newCs) => {
  return newCs
    ? newCs?.map((cs) => {
      const currentElement = current.find((curr) => curr.id === cs.id) || {}
      return {
        ...currentElement,
        ...cs,
        notes: cs?.notes || currentElement?.notes,
      }
    })
    : current
}

export const useCallSegments = () => {
  const { userData } = useAuth()
  const { answeredCall, expectingAnswer } = useCall()
  const { additionalUserData } = useProfile()

  const [callSegments, setCallSegments] = useState([])

  const showSegment = useCallback(
    (cs) => {
      if ((cs.type || cs.callSegmentType) === UNKNOWN) {
        return false
      }
      if (cs.participantMode === "LISTEN" && userData.role !== "SUPERVISOR") {
        return false
      }
      const inHouseUser = !additionalUserData?.microCallCenter
      const inHouseSegment = !cs?.interpreter?.external && !cs?.agentMicroCallCenter
      if (inHouseUser === inHouseSegment) {
        return true
      }
      if ([OVER_PHONE, OUTSOURCE].includes(cs.type || cs.callSegmentType)) {
        return true
      }
    },
    [userData?.role, additionalUserData?.microCallCenter]
  )

  const parseAPICallSegments = useCallback(
    (response) => {
      return response?.filter?.(showSegment)?.map((cs) => {
        const {
          id,
          callId,
          name,
          notes,
          languageId,
          callSegmentType,
          agentId,
          channel,
          agentName = "",
          interpreterName = "",
          outsourcePartnerName,
        } = cs
        return {
          id,
          callId,
          name,
          notes,
          languageId,
          type: callSegmentType,
          partnerName: outsourcePartnerName,
          interpreterId: agentId || additionalUserData.id,
          interpreterName: agentName || interpreterName,
          channel,
        }
      })
    },
    [showSegment, additionalUserData]
  )

  const onAPICallSegmentsReceived = usePureCallback((newSegments) => {
    const parsed = parseAPICallSegments(newSegments)
    setCallSegments((current) => mergeCallSegments(current, parsed))
  })

  const fetchAPICallSegments = usePureCallback(async (connected) => {
    if (!connected || !answeredCall?.interaction) {
      return null
    }
    try {
      const response = await dataService.Interactions.CallSegments.get({ interactionId: answeredCall.interaction.id })
      logger.debug("Call Segments fetched", response?.data?.map(toRedacted))
      onAPICallSegmentsReceived(response?.data)
    } catch (err) {
      logger.error("Unable to fetch call segments", err)
    }
  })

  const parseSSECallSegments = useCallback(
    (callSegments) => {
      return callSegments?.filter?.(showSegment).map((cs) => {
        const {
          callSegmentId,
          callSegmentName,
          notes,
          language,
          type,
          channel,
          interpreter,
          fullName = "",
          interaction,
          outsourcePartner,
        } = cs
        return {
          id: callSegmentId,
          callId: interaction?.interactionId,
          name: callSegmentName,
          notes,
          languageId: language?.languageId,
          type,
          partnerName: outsourcePartner?.name,
          interpreterId: interpreter?.interpreterId || additionalUserData.id,
          interpreterName: fullName,
          channel: channel,
        }
      })
    },
    [showSegment, additionalUserData]
  )

  const onSSECallSegmentsReceived = usePureCallback((newSegments) => {
    const parsed = parseSSECallSegments(newSegments)
    setCallSegments((current) => mergeCallSegments(current, parsed))
  })

  useEffect(() => {
    if (!answeredCall?.interaction?.id) {
      return
    }

    SSE.on("call-segments", onSSECallSegmentsReceived)
    SSE.on("connect", fetchAPICallSegments)
    SSE.subscribe("call-segments", answeredCall?.interaction?.id)
    return () => {
      SSE.off("call-segments", onSSECallSegmentsReceived)
      SSE.unsubscribe("call-segments")
      SSE.off("connect", fetchAPICallSegments)
      setCallSegments([])
    }
  }, [answeredCall?.interaction?.id, onSSECallSegmentsReceived, fetchAPICallSegments, onAPICallSegmentsReceived])

  useEffect(() => {
    // PI-469: At the start of calls, there is a race between the media server and the agent client/notification server
    // In some cases, by the time the request to subscribe to the call segments was received by the server, the media
    // server had already dispatched events for the first participants joining, therefore the agent client would miss
    // the call segment updated events triggered by those media server events.
    // In that case the call sheet is stuck with no call segments to display at the start of the call.
    // Here we use the expectingAnswer value from the call context, which changes when the webrtc CALL_STARTED event fires
    // If the call does not have call segments by the time the webrtc call starts, we fetch them again
    if (answeredCall?.interaction?.id && expectingAnswer === false && !callSegments.length) {
      dataService.Interactions.CallSegments.get({ interactionId: answeredCall.interaction.id })
        .then((response) => {
          logger.debug("Call Segments fetched", response?.data?.map(toRedacted))
          onAPICallSegmentsReceived(response?.data)
        })
        .catch((err) => {
          logger.error("Unable to fetch call segments", err)
        })
    }

  }, [callSegments, expectingAnswer, answeredCall?.interaction?.id, onAPICallSegmentsReceived]);

  return {
    callSegments,
  }
}
